import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X, Upload, Image as ImageIcon, Zap, Code } from "lucide-react";
import { extractApiError } from "../../lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RouteFormModal({ route, onClose, onSuccess }) {
  const isEdit = !!route;
  const [form, setForm] = useState({
    slug: route?.slug || "", category: route?.category || "", origin: route?.origin || "",
    destination: route?.destination || "", base_price: route?.base_price?.toString() || "",
    duration_hours: route?.duration_hours?.toString() || "", distance_km: route?.distance_km?.toString() || "",
    meta_title: route?.meta_title || "", meta_description: route?.meta_description || "",
    h1_heading: route?.h1_heading || "", keywords: route?.keywords || "", seo_priority: route?.seo_priority || "Medium",
    header_image_url: route?.header_image_url || "",
    json_ld: route?.json_ld || "",
    immediate_confirmation: !!route?.immediate_confirmation,
    immediate_confirmation_except_vehicle_ids: route?.immediate_confirmation_except_vehicle_ids || [],
    immediate_confirmation_except_custom_address: !!route?.immediate_confirmation_except_custom_address,
    venice_hotel_surcharge_discount: route?.venice_hotel_surcharge_discount?.toString() || "",
    venice_airport_reduction: route?.venice_airport_reduction?.toString() || "",
    disabled: !!route?.disabled
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/vehicles`)
      .then((r) => setVehicles(r.data || []))
      .catch(() => { /* non-blocking */ });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.category || !form.origin || !form.destination || !form.base_price) {
      toast.error("Please fill required fields"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        base_price: parseFloat(form.base_price),
        duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : null,
        distance_km: form.distance_km ? parseInt(form.distance_km) : null,
        header_image_url: form.header_image_url || null,
        json_ld: form.json_ld || "",
        immediate_confirmation: !!form.immediate_confirmation,
        immediate_confirmation_except_vehicle_ids: form.immediate_confirmation_except_vehicle_ids || [],
        immediate_confirmation_except_custom_address: !!form.immediate_confirmation_except_custom_address,
        venice_hotel_surcharge_discount: form.venice_hotel_surcharge_discount
          ? Math.max(0, parseFloat(form.venice_hotel_surcharge_discount) || 0)
          : 0,
        venice_airport_reduction: form.venice_airport_reduction
          ? Math.max(0, parseFloat(form.venice_airport_reduction) || 0)
          : 0,
        disabled: !!form.disabled,
      };
      if (isEdit) { await axios.put(`${API}/admin/routes/${route.route_id}`, payload); toast.success("Route updated!"); }
      else { await axios.post(`${API}/admin/routes`, payload); toast.success("Route created!"); }
      onSuccess();
    } catch (error) { toast.error(extractApiError(error, "Failed to save route")); }
    finally { setSubmitting(false); }
  };

  const u = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" },
      });
      u("header_image_url", res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(extractApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const previewSrc = form.header_image_url
    ? (form.header_image_url.startsWith("http") ? form.header_image_url : `${BACKEND_URL}${form.header_image_url}`)
    : null;

  const [loadingJsonLdDefault, setLoadingJsonLdDefault] = useState(false);
  const insertJsonLdDefault = async () => {
    if (!isEdit) {
      toast.info("Save the route first, then come back to preview the default JSON-LD.");
      return;
    }
    setLoadingJsonLdDefault(true);
    try {
      const res = await axios.get(`${API}/admin/routes/${route.route_id}/json-ld-default`);
      u("json_ld", res.data.json_ld || "");
      toast.success("Default JSON-LD loaded — review and save to override.");
    } catch (err) {
      toast.error(extractApiError(err, "Failed to load default JSON-LD"));
    } finally {
      setLoadingJsonLdDefault(false);
    }
  };
  const validateJsonLd = () => {
    const raw = (form.json_ld || "").trim();
    if (!raw) { toast.info("Empty — the public page will use the auto-generated default."); return; }
    try {
      JSON.parse(raw);
      toast.success("Valid JSON.");
    } catch (e) {
      toast.error(`Invalid JSON: ${e.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="route-form-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">{isEdit ? "Edit Route" : "Add New Route"}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-white/50 text-xs mb-1 block">Origin *</label><Input value={form.origin} onChange={(e) => u("origin", e.target.value)} className="input-dark" placeholder="Venice" /></div>
            <div><label className="text-white/50 text-xs mb-1 block">Destination *</label><Input value={form.destination} onChange={(e) => u("destination", e.target.value)} className="input-dark" placeholder="Rome" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-white/50 text-xs mb-1 block">Slug *</label><Input value={form.slug} onChange={(e) => u("slug", e.target.value)} className="input-dark" placeholder="venice-to-rome" /></div>
            <div><label className="text-white/50 text-xs mb-1 block">Category *</label><Input value={form.category} onChange={(e) => u("category", e.target.value)} className="input-dark" placeholder="Cities" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-white/50 text-xs mb-1 block">Price ({"\u20AC"}) *</label><Input type="number" value={form.base_price} onChange={(e) => u("base_price", e.target.value)} className="input-dark" /></div>
            <div><label className="text-white/50 text-xs mb-1 block">Distance (km)</label><Input type="number" value={form.distance_km} onChange={(e) => u("distance_km", e.target.value)} className="input-dark" /></div>
            <div><label className="text-white/50 text-xs mb-1 block">Duration (h)</label><Input type="number" step="0.5" value={form.duration_hours} onChange={(e) => u("duration_hours", e.target.value)} className="input-dark" /></div>
          </div>

          {/* Header Image upload */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4">
            <label className="text-white/60 text-xs uppercase mb-2 block flex items-center gap-2">
              <ImageIcon className="w-3.5 h-3.5" /> Header Image
            </label>
            {previewSrc ? (
              <div className="relative">
                <img
                  src={previewSrc}
                  alt="Header preview"
                  className="w-full h-40 object-cover rounded-sm border border-white/10"
                  data-testid="route-image-preview"
                />
                <button
                  type="button"
                  onClick={() => u("header_image_url", "")}
                  className="absolute top-2 right-2 bg-black/70 text-white/80 hover:text-red-400 p-1 rounded-sm"
                  data-testid="route-image-remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed border-white/10 rounded-sm cursor-pointer hover:border-[#C5A059]/40 transition-colors" data-testid="route-image-uploader">
                <Upload className="w-6 h-6 text-white/30" />
                <span className="text-white/40 text-xs">{uploading ? "Uploading..." : "Click to upload (jpg/png/webp, max 5 MB)"}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onPickImage} className="hidden" disabled={uploading} data-testid="route-image-input" />
              </label>
            )}
          </div>

          <div><label className="text-white/50 text-xs mb-1 block">SEO Priority</label>
            <Select value={form.seo_priority} onValueChange={(v) => u("seo_priority", v)}>
              <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#333]">{["Top", "High", "Medium", "Low"].map(p => <SelectItem key={p} value={p} className="text-white hover:bg-[#333]">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-white/50 text-xs mb-1 block">SEO Title</label><Input value={form.meta_title} onChange={(e) => u("meta_title", e.target.value)} className="input-dark" /></div>
          <div><label className="text-white/50 text-xs mb-1 block">Meta Description</label><Input value={form.meta_description} onChange={(e) => u("meta_description", e.target.value)} className="input-dark" /></div>
          <div><label className="text-white/50 text-xs mb-1 block">Keywords</label><Input value={form.keywords} onChange={(e) => u("keywords", e.target.value)} className="input-dark" /></div>

          {/* Reduce the Hotel-in-Venice per-leg surcharge AND the "+ Water Taxi"
              option per-leg cost on this specific route. Applies to both:
              (a) the Hotel-in-Venice "surcharge" mode (destination is a hotel and
              the route origin is a non-Venice city), and (b) any booking where
              the customer picks "+ Water Taxi" as pickup or drop-off. Clamped at 0. */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4">
            <label className="text-white/60 text-xs uppercase mb-1 block">Reduce Water Taxi cost ({"\u20AC"})</label>
            <Input
              type="number"
              min="0"
              step="1"
              value={form.venice_hotel_surcharge_discount}
              onChange={(e) => u("venice_hotel_surcharge_discount", e.target.value)}
              placeholder="0"
              className="input-dark"
              data-testid="route-water-taxi-discount"
            />
            <p className="text-white/40 text-[11px] mt-1">
              Subtracted per leg from both the Hotel-in-Venice surcharge and the &ldquo;+ Water Taxi&rdquo; option when this route is booked. Set 0 to keep the full prices.
            </p>
          </div>

          <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4">
            <label className="text-white/60 text-xs uppercase mb-1 block">Venice / Airport reduction ({"\u20AC"})</label>
            <Input
              type="number"
              min="0"
              step="1"
              value={form.venice_airport_reduction}
              onChange={(e) => u("venice_airport_reduction", e.target.value)}
              placeholder="0"
              className="input-dark"
              data-testid="route-venice-airport-reduction"
            />
            <p className="text-white/40 text-[11px] mt-1">
              Hidden reduction subtracted from this route&apos;s price per leg (&times;2 on round trips) when From or To is Venice or an Airport. Customers only see the lower price. Set 0 to disable.
            </p>
          </div>

          {/* JSON-LD (Schema.org) structured data — for GEO / AI indexing */}
          <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4" data-testid="route-jsonld-block">
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/60 text-xs uppercase flex items-center gap-2">
                <Code className="w-3.5 h-3.5 text-[#C5A059]" />
                Schema.org JSON-LD
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={insertJsonLdDefault}
                  disabled={loadingJsonLdDefault || !isEdit}
                  className="text-[10px] px-2 py-1 rounded-sm border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="route-jsonld-insert-default"
                  title={isEdit ? "Load auto-generated TaxiService + LocalBusiness JSON-LD" : "Save the route first"}
                >
                  {loadingJsonLdDefault ? "Loading..." : "Insert default"}
                </button>
                <button
                  type="button"
                  onClick={validateJsonLd}
                  className="text-[10px] px-2 py-1 rounded-sm border border-white/20 text-white/70 hover:bg-white/5"
                  data-testid="route-jsonld-validate"
                  title="Check that the current content is valid JSON"
                >
                  Validate
                </button>
                <button
                  type="button"
                  onClick={() => u("json_ld", "")}
                  className="text-[10px] px-2 py-1 rounded-sm border border-rose-400/30 text-rose-300 hover:bg-rose-400/10"
                  data-testid="route-jsonld-clear"
                  title="Clear and fall back to the auto-generated default"
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              value={form.json_ld}
              onChange={(e) => u("json_ld", e.target.value)}
              placeholder='Leave empty to use the auto-generated default (TaxiService + LocalBusiness).\nOr paste a full JSON object like {"@context":"https://schema.org", ... }'
              className="input-dark w-full font-mono text-[11px] leading-relaxed"
              rows={form.json_ld ? 14 : 4}
              spellCheck={false}
              data-testid="route-jsonld-textarea"
            />
            <p className="text-white/40 text-[10px] mt-1.5 leading-relaxed">
              Injected into the route landing page as <code className="text-[#C5A059]">{'<script type="application/ld+json">'}</code>.
              Leave empty → we auto-generate a sensible default from origin/destination/price. Override this box to fully customize what Google / AI crawlers see.
            </p>
          </div>

          {/* Immediate confirmation toggle */}
          <label
            className="flex items-start gap-3 bg-[#0F0F0F] border border-white/5 rounded-sm p-4 cursor-pointer hover:border-[#C5A059]/40 transition-colors"
            data-testid="route-immediate-confirmation-toggle"
          >
            <input
              type="checkbox"
              checked={!!form.immediate_confirmation}
              onChange={(e) => u("immediate_confirmation", e.target.checked)}
              className="mt-1 accent-[#C5A059] w-4 h-4"
              data-testid="route-immediate-confirmation-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-white text-sm font-medium">
                <Zap className="w-4 h-4 text-[#C5A059]" />
                Immediate confirmation
              </div>
              <p className="text-white/40 text-xs mt-1 leading-relaxed">
                When ON, customer requests for this route skip the "Pending" review step and go straight to <strong className="text-blue-400">Awaiting Payment</strong> — the Stripe payment link email is sent automatically. Use this for low-risk routes where you always honor the listed price.
              </p>
            </div>
          </label>

          {/* Sub-options - shown only when immediate_confirmation is ON */}
          {form.immediate_confirmation && (
            <div className="ml-8 -mt-2 space-y-3" data-testid="route-immediate-exceptions">
              {/* Exception 1: multi-select of vehicles */}
              <div data-testid="route-except-vehicles-block">
                <label className="block text-white/70 text-xs mb-2">
                  <strong>Except if</strong> the assigned vehicle is one of:
                </label>
                {vehicles.length === 0 ? (
                  <p className="text-white/40 text-[10px]">No vehicles defined yet. Add some in the Vehicles tab.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {vehicles.map((v) => {
                      const selected = (form.immediate_confirmation_except_vehicle_ids || []).includes(v.vehicle_id);
                      return (
                        <label
                          key={v.vehicle_id}
                          className={`text-[11px] px-2 py-1 rounded-sm border cursor-pointer transition-colors ${selected ? "bg-amber-400/20 text-amber-200 border-amber-400/40" : "bg-[#0F0F0F] text-white/60 border-white/10 hover:border-white/30"}`}
                          data-testid={`route-except-vehicle-${v.vehicle_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const set = new Set(form.immediate_confirmation_except_vehicle_ids || []);
                              if (e.target.checked) set.add(v.vehicle_id); else set.delete(v.vehicle_id);
                              u("immediate_confirmation_except_vehicle_ids", Array.from(set));
                            }}
                            className="hidden"
                          />
                          {v.title}
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-white/30 text-[10px] mt-1.5">
                  If the auto-assigned vehicle ends up in this list, the booking falls back to "Pending" instead of going straight to Awaiting Payment.
                </p>
              </div>

              {/* Exception 2: specific address (any side, includes Venice - Near Venice) */}
              <label className="flex items-start gap-2 text-white/70 text-xs cursor-pointer" data-testid="route-except-custom-address-label">
                <input
                  type="checkbox"
                  checked={!!form.immediate_confirmation_except_custom_address}
                  onChange={(e) => u("immediate_confirmation_except_custom_address", e.target.checked)}
                  className="mt-0.5 accent-[#C5A059] w-3.5 h-3.5"
                  data-testid="route-except-custom-address"
                />
                <span>
                  <strong>Except</strong> if the pickup OR drop-off has a specific address set
                  <span className="block text-white/40 text-[10px] mt-0.5">Includes "Venice - Near Venice" and any non-Venice "Specific address" toggle. Forces "Pending" so an operator can validate.</span>
                </span>
              </label>
            </div>
          )}

          {/* Disabled toggle — hide route from public lists */}
          <label className="flex items-start gap-3 cursor-pointer py-2" data-testid="route-disabled-label">
            <input
              type="checkbox"
              checked={!!form.disabled}
              onChange={(e) => u("disabled", e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-rose-400"
              data-testid="route-disabled"
            />
            <div className="flex-1">
              <div className="text-white text-sm font-medium flex items-center gap-2">
                <X className="w-4 h-4 text-rose-400" /> Disable route
              </div>
              <p className="text-white/40 text-xs mt-1 leading-relaxed">
                When ON, this route is hidden from the homepage booking form and the routes listing page. Existing bookings keep their data intact. Customers attempting to load the slug directly get a 404.
              </p>
            </div>
          </label>

          <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="route-form-submit">{submitting ? "Saving..." : isEdit ? "Update Route" : "Create Route"}</Button>
        </form>
      </div>
    </div>
  );
}
